/**
 * Geometria do eixo do tempo (funções puras, testadas): domínio, marcas de
 * hora, buracos entre quadros, faixas de disponibilidade e conversão entre
 * posição (0–1) e quadro. O componente `TimeAxis` só desenha.
 *
 * Brasília é UTC−3 sem horário de verão: as fronteiras de hora em Brasília
 * coincidem com as fronteiras de hora em UTC, e a meia-noite de Brasília é
 * 03:00 UTC.
 */
import type { TimeRange } from '../state/time';

export const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const BRT_MIDNIGHT_UTC_MS = 3 * HOUR;

export interface AxisDomain {
  start: number;
  end: number;
  /**
   * O instante "agora" cai dentro do eixo. Com só imagens observadas o eixo
   * termina na última delas, então isto só acontece com o relógio do aparelho
   * atrasado — e voltará a ser o caso normal quando houver dado no futuro
   * (modelos de previsão), marcando a fronteira entre observado e previsto.
   */
  nowInside: boolean;
}

/**
 * Domínio do eixo: do PRIMEIRO ao ÚLTIMO quadro do loop. A última imagem fica
 * no fim da barra, e o rótulo de cada ponta é mesmo o instante que está ali —
 * antes o eixo ia até a janela pedida (ou até "agora"), e sobrava à direita um
 * trecho vazio que parecia tempo com imagem. A espera pela próxima imagem é
 * dita em texto ("mais recente · há N min"), não como espaço no eixo.
 *
 * Sem quadros (ainda carregando), usa a janela do loop só para o eixo existir.
 */
export function axisDomain(frames: readonly number[], range: TimeRange | null, now: number): AxisDomain | null {
  const first = frames[0] ?? range?.start;
  const last = frames[frames.length - 1] ?? range?.end;
  if (first === undefined || last === undefined) return null;
  const end = Math.max(first + 1, last);
  return { start: first, end, nowInside: now >= first && now <= end };
}

/** Posição (0–1) de um instante no domínio. */
export function positionOf(t: number, d: AxisDomain): number {
  const p = (t - d.start) / (d.end - d.start);
  return Math.max(0, Math.min(1, p));
}

export interface Tick {
  t: number;
  /** Meia-noite em Brasília (fronteira de dia). */
  major: boolean;
}

const TICK_STEPS = [10 * 60_000, 30 * 60_000, HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY];

/** Marcas alinhadas à hora de Brasília, no maior passo que dê no máximo `maxTicks`. */
export function hourTicks(d: AxisDomain, maxTicks = 26): Tick[] {
  const span = d.end - d.start;
  const step = TICK_STEPS.find((s) => span / s <= maxTicks) ?? DAY;
  const ticks: Tick[] = [];
  const firstTick = Math.ceil(d.start / step) * step;
  for (let t = firstTick; t <= d.end; t += step) {
    ticks.push({ t, major: (t - BRT_MIDNIGHT_UTC_MS) % DAY === 0 });
  }
  return ticks;
}

/** Trechos entre quadros consecutivos maiores que 1,5 passo: imagens que faltaram. */
export function frameGaps(frames: readonly number[], stepMs: number): Array<[number, number]> {
  const gaps: Array<[number, number]> = [];
  for (let i = 1; i < frames.length; i++) {
    if (frames[i] - frames[i - 1] > stepMs * 1.5) gaps.push([frames[i - 1] + stepMs, frames[i]]);
  }
  return gaps;
}

/**
 * Trechos em que uma camada NÃO tem imagem (a partir dos instantes por quadro
 * do plano). Cada trecho vai do primeiro quadro sem imagem até o quadro em que
 * a camada volta — assim uma falta isolada ainda tem largura no eixo.
 */
export function missingSegments(times: readonly (number | null)[], frames: readonly number[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const last = frames.length - 1;
  if (last < 0) return out;
  let startIdx = -1;
  for (let i = 0; i <= frames.length; i++) {
    const missing = i < frames.length && (times[i] === undefined || times[i] === null);
    if (missing && startIdx < 0) startIdx = i;
    if (!missing && startIdx >= 0) {
      out.push([frames[startIdx], frames[Math.min(i, last)]]);
      startIdx = -1;
    }
  }
  return out;
}

/** Índice do quadro mais próximo do instante `t`. */
export function nearestFrameIndex(frames: readonly number[], t: number): number {
  if (frames.length === 0) return 0;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid] < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(frames[lo - 1] - t) <= Math.abs(frames[lo] - t)) return lo - 1;
  return lo;
}

/** Quadro mais próximo de uma posição (0–1) do eixo. */
export function frameAtPosition(frames: readonly number[], d: AxisDomain, p: number): number {
  const t = d.start + Math.max(0, Math.min(1, p)) * (d.end - d.start);
  return nearestFrameIndex(frames, t);
}
